import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { ShieldWarning, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

const SEV = { high: "bg-red-100 text-red-700 border-red-300", medium: "bg-amber-100 text-amber-700 border-amber-300", low: "bg-surface-muted text-ink-muted border-border" };

export default function AuditPage() {
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [runs, setRuns] = useState([]);

  const loadRuns = async () => { try { const { data } = await api.get("/audit/runs"); setRuns(data); } catch (err) { console.error('Audit runs load failed:', err); } };
  useEffect(() => { loadRuns(); }, []);

  const load = async (status = showResolved ? "resolved" : "open") => {
    const { data } = await api.get("/audit/alerts", { params: { status } });
    setAlerts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showResolved]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/audit/run", {}, { timeout: 180000 });
      toast.success(`Audit complete — ${data.open_alerts} open alert(s)`);
      setShowResolved(false);
      await load("open");
      await loadRuns();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setRunning(false); }
  };

  const resolve = async (id) => {
    try { await api.post(`/audit/alerts/${id}/resolve`); toast.success("Marked resolved"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="audit-page">
      <PageHeader title="AI Audit Alerts" subtitle="AI + rule checks across all entries: duplicate bills, GST mismatches, backdated payments, cash-limit breaches and suspicious patterns." />
      <div className="p-8 max-w-5xl space-y-5">
        <div className="card-base p-5 flex items-center gap-4 flex-wrap">
          <button onClick={run} disabled={running} className="btn-primary inline-flex items-center gap-1.5" data-testid="audit-run-btn">
            {running ? <ArrowsClockwise size={14} className="animate-spin" weight="bold" /> : <ShieldWarning size={14} weight="bold" />}
            {running ? "AI auditing all entries…" : "Run AI Audit Now"}
          </button>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} data-testid="audit-show-resolved" /> Show resolved
          </label>
          <span className="status-pill status-success" data-testid="audit-schedule-badge">Weekly auto-audit: ON</span>
          {runs[0] && <span className="text-xs text-ink-muted">Last run: {new Date(runs[0].ran_at).toLocaleString("en-IN")} ({runs[0].trigger})</span>}
        </div>

        {runs.length > 0 && (
          <div className="card-base p-4" data-testid="audit-run-history">
            <div className="label-overline mb-2">Audit Run History</div>
            <div className="space-y-1 max-h-44 overflow-auto">
              {runs.map(r => (
                <div key={r.id} className="flex items-center justify-between text-xs border-b border-border/50 py-1 last:border-0">
                  <span className="tabular">{new Date(r.ran_at).toLocaleString("en-IN")}</span>
                  <span className={`status-pill ${r.trigger === "scheduled" ? "status-info" : "status-neutral"}`}>{r.trigger}</span>
                  <span>{r.findings} finding(s) · {r.open_alerts} open</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3" data-testid="audit-alerts-list">
          {alerts.length === 0 && <div className="card-base p-8 text-center text-ink-muted text-sm">No {showResolved ? "resolved" : "open"} alerts. Run an audit to scan all entries.</div>}
          {alerts.map(a => (
            <div key={a.id} className={`card-base p-4 border ${SEV[a.severity] ? "" : ""}`} data-testid={`audit-alert-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${SEV[a.severity] || SEV.low}`}>{a.severity}</span>
                    <span className="status-pill status-neutral">{a.category}</span>
                  </div>
                  <div className="font-semibold text-sm">{a.title}</div>
                  <div className="text-xs text-ink-muted mt-0.5">{a.detail}</div>
                </div>
                {a.status === "open" && (
                  <button onClick={() => resolve(a.id)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`resolve-${a.id}`}>
                    <CheckCircle size={13} /> Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
